import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  User,
  Settings as SettingsIcon,
  Trophy,
  Plug,
  Trash2,
  CreditCard,
  Monitor,
  Moon,
  Sun,
} from 'lucide-react';
import { Switch } from '@headlessui/react';
import { useProfile, useRacePlan } from '../hooks/useProfile';
import Loader from './Loader';
import { ConnectStrava } from '../assets';
import VersionLabel from '../components/VersionLabel';
import { ThemePreference, useTheme } from '../contexts/ThemeContext';
import { useDisconnectIntegration } from '../hooks/mutations/useDisconnectIntegration';
import type { DisconnectableIntegration } from '../hooks/mutations/useDisconnectIntegration';
import { useGarminOAuth } from '../hooks/useGarminOAuth';
import { useUpdateUserSettings } from '../hooks/mutations/useUpdateUserSettings';
import { useDeletePlan } from '../hooks/mutations/useDeletePlan';

function Settings() {
  const { preference: themePreference, setPreference: setThemePreference } =
    useTheme();
  const {
    data: profile,
    isLoading: profileLoading,
    isFetching: profileFetching,
  } = useProfile();
  const { data: racePlan } = useRacePlan();
  const { connect: connectGarmin, isStarting: isStartingGarmin } =
    useGarminOAuth();
  const customerPortalLink = import.meta.env
    .REACT_APP_STRIPE_CUSTOMER_PORTAL_LINK;
  const [localSettings, setLocalSettings] = useState<any>();
  const [changesMade, setChangesMade] = useState(false);
  const [saveState, setSaveState] = useState<'saved' | 'saving' | 'error'>('saved');
  const disconnectIntegration = useDisconnectIntegration({
    onError: (error) => window.dispatchEvent(new CustomEvent('showToast', { detail: { message: error.message, type: 'error' } })),
  });
  const updateSettingsMutation = useUpdateUserSettings({
    onSuccess: () => { setChangesMade(false); setSaveState('saved'); },
    onError: () => setSaveState('error'),
  });
  const deletePlanMutation = useDeletePlan();
  const connectedIntegrations = profile?.user?.connections || [];
  const hasStravaConnection = connectedIntegrations.some(
    (connection: any) => connection.connectionType === 'strava',
  );
  const hasGarminConnection = connectedIntegrations.some(
    (connection: any) => connection.connectionType === 'garmin',
  );
  const hasAnyConnection =
    connectedIntegrations.length > 0 || Boolean(profile?.user?.connectionId);
  const debounceTimer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (profile?.user) {
      const { titleEnabled, descriptionEnabled, language, updatedAt } =
        profile.user;
      setLocalSettings({
        titleEnabled: Boolean(titleEnabled),
        descriptionEnabled: Boolean(descriptionEnabled),
        language,
        updatedAt,
      });
    }
  }, [profile]);

  useEffect(() => {
    if (profile?.user) {
      const profileSettings = {
        titleEnabled: Boolean(profile.user.titleEnabled),
        descriptionEnabled: Boolean(profile.user.descriptionEnabled),
        language: profile.user.language,
        updatedAt: profile.user.updatedAt,
      };
      setChangesMade(
        JSON.stringify(localSettings) !== JSON.stringify(profileSettings),
      );
    }
  }, [localSettings, profile]);

  const updateUserSettings = () => {
    if (!updateSettingsMutation.isPending) {
      setSaveState('saving');
      updateSettingsMutation.mutate(localSettings);
    }
  };

  const deletePlan = () => {
    if (!racePlan) {
      return;
    }
    deletePlanMutation.mutate(racePlan.id);
  };

  const confirmDisconnect = (connectionType: DisconnectableIntegration) => {
    const displayName = connectionType === 'strava' ? 'Strava' : 'Garmin';
    if (
      !window.confirm(
        `Disconnect ${displayName}? New activities and workouts will no longer sync.`,
      )
    ) {
      return;
    }

    disconnectIntegration.mutate(connectionType);
  };

  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (changesMade) {
        updateUserSettings();
      }
    }, 500);
  }, [changesMade]);

  if (profileLoading || profileFetching) {
    return <Loader />;
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-1 md:px-0">
      <div className="flex items-start justify-between gap-4 px-1 sm:px-0">
        <div>
          <p className="eyebrow">Account & preferences</p>
          <h1 className="mt-2 text-3xl font-bold tracking-[-0.045em] text-brand-midnight dark:text-white">Settings</h1>
        </div>
        <p className="pt-1 text-right text-xs font-semibold text-gray-500" role="status">
          {saveState === 'saving' ? 'Saving...' : saveState === 'error' ? 'Could not save' : 'All changes saved'}
        </p>
      </div>
      <div className="flex items-center gap-3 px-1 py-1 sm:px-0">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-blueSoft text-brand-cobalt">
          {profile?.user?.picture ? (
            <img
              src={profile.user.picture}
              alt={profile?.user?.name || 'Profile'}
              className="h-full w-full object-cover"
            />
          ) : (
            <User className="h-6 w-6" />
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-brand-midnight dark:text-white">{profile?.user?.name || 'Your profile'}</p>
          <p className="truncate text-xs text-gray-500">{profile?.user?.email}</p>
        </div>
      </div>

      <section className="surface-card p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sun className="h-5 w-5 text-gray-500 dark:text-gray-300" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Appearance
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-300">
              Choose how RaceTrainer looks on this device.
            </p>
          </div>
        </div>
        <div
          className="grid grid-cols-3 gap-2 rounded-xl bg-gray-100 dark:bg-black/20 p-1.5"
          role="radiogroup"
          aria-label="Color theme"
        >
          {(
            [
              { value: 'light', label: 'Light', icon: Sun },
              { value: 'dark', label: 'Dark', icon: Moon },
              { value: 'system', label: 'System', icon: Monitor },
            ] as const
          ).map(({ value, label, icon: Icon }) => {
            const selected = themePreference === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setThemePreference(value as ThemePreference)}
                className={`flex min-h-11 items-center justify-center gap-2 rounded-lg px-2 py-2 text-sm font-semibold transition-colors ${
                  selected
                    ? 'bg-brand-midnight text-white shadow-sm dark:bg-white dark:text-brand-midnight'
                    : 'text-gray-600 hover:bg-white/70 dark:text-gray-300 dark:hover:bg-white/10'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <div className="surface-card p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <SettingsIcon className="h-5 w-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">
            Workout settings
          </h2>
        </div>
        <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
          <div className="flex items-center justify-between gap-6">
            <div>
              <label className="text-sm font-semibold text-gray-900">
                Generated titles
              </label>
              <p className="text-sm text-gray-500">
                Automatically generate titles for synced workouts.
              </p>
            </div>
            <Switch
              checked={Boolean(hasAnyConnection && localSettings?.titleEnabled)}
              disabled={!hasAnyConnection}
              onChange={(value: boolean) =>
                setLocalSettings({ ...localSettings, titleEnabled: value })
              }
              className="group inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-gray-200 data-[checked]:bg-brand-volt data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
            >
              <span className="size-4 translate-x-1 rounded-full bg-white transition group-data-[checked]:translate-x-6" />
            </Switch>
          </div>

          <div className="flex items-center justify-between gap-6">
            <div>
              <label className="text-sm font-semibold text-gray-900">
                Generated summaries
              </label>
              <p className="text-sm text-gray-500">
                Automatically generate summaries for synced workouts.
              </p>
            </div>
            <Switch
              checked={Boolean(hasAnyConnection && localSettings?.descriptionEnabled)}
              disabled={!hasAnyConnection}
              onChange={(value: boolean) =>
                setLocalSettings({
                  ...localSettings,
                  descriptionEnabled: value,
                })
              }
              className="group inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-gray-200 data-[checked]:bg-brand-volt data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
            >
              <span className="size-4 translate-x-1 rounded-full bg-white transition group-data-[checked]:translate-x-6" />
            </Switch>
          </div>
        </form>
      </div>

      <div className="surface-card p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Plug className="h-5 w-5 text-gray-500 dark:text-gray-300" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Integrations
          </h2>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Strava
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-300">
                Connect your Strava account to sync activities.
              </p>
            </div>
            {hasStravaConnection ? (
              <button
                type="button"
                disabled={disconnectIntegration.isPending}
                onClick={() => confirmDisconnect('strava')}
                className="shrink-0 rounded-md border border-red-300 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-wait disabled:opacity-60 dark:border-red-400 dark:text-red-300 dark:hover:bg-red-950/30"
              >
                {disconnectIntegration.variables === 'strava'
                  ? disconnectIntegration.isPending
                    ? 'Disconnecting…'
                    : 'Disconnect'
                  : 'Disconnect'}
              </button>
            ) : (
              <Link
                className="shrink-0"
                to={`${import.meta.env.REACT_APP_API_SERVICE_URI}/auth/strava?email=${encodeURIComponent(profile?.user?.email)}&from=${encodeURIComponent(
                  window.location.origin + '/settings',
                )}`}
              >
                <img
                  src={ConnectStrava}
                  alt="Connect with Strava"
                  className="h-10"
                />
              </Link>
            )}
          </div>

          <div className="flex items-center justify-between gap-6 border-t border-gray-100 pt-4 dark:border-white/20">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Garmin Connect
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-300">
                Connect Garmin to sync workouts from your device.
              </p>
            </div>
            {hasGarminConnection ? (
              <button
                type="button"
                disabled={disconnectIntegration.isPending}
                onClick={() => confirmDisconnect('garmin')}
                className="shrink-0 rounded-md border border-red-300 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-wait disabled:opacity-60 dark:border-red-400 dark:text-red-300 dark:hover:bg-red-950/30"
              >
                {disconnectIntegration.variables === 'garmin'
                  ? disconnectIntegration.isPending
                    ? 'Disconnecting…'
                    : 'Disconnect'
                  : 'Disconnect'}
              </button>
            ) : (
              <button
                type="button"
                disabled={isStartingGarmin}
                onClick={() => void connectGarmin('/settings')}
                className="shrink-0 rounded-md bg-brand-midnight px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700 dark:bg-brand-cobalt dark:hover:bg-brand-cobalt/80"
              >
                {isStartingGarmin ? 'Connecting…' : 'Connect Garmin'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="surface-card p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="h-5 w-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Plan details</h2>
        </div>
        {racePlan ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-gray-500 mb-1">Race</div>
                <div className="font-medium text-gray-900">
                  {racePlan.race.name}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Race date</div>
                <div className="font-medium text-gray-900">
                  {new Date(racePlan.race.date).toLocaleDateString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Distance</div>
                <div className="font-medium text-gray-900">
                  {racePlan.race.distance}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                if (
                  window.confirm(
                    'Are you sure you want to delete your plan? This cannot be undone.',
                  )
                ) {
                  deletePlan();
                }
              }}
              className="inline-flex items-center gap-2 text-sm px-4 py-2 text-red-700 bg-red-100 rounded-lg hover:bg-red-200"
            >
              <Trash2 className="h-4 w-4" />
              Delete Plan
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No active training plan. Create a plan to get started.
          </p>
        )}
      </div>

      <section className="rounded-xl bg-white p-6 shadow dark:bg-brand-midnight dark:ring-1 dark:ring-slate-700">
        <div className="mb-4 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-gray-500 dark:text-gray-300" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Membership
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-300">
              Manage your RaceTrainer subscription.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-start justify-between gap-4 border-t border-gray-100 pt-4 sm:flex-row sm:items-center dark:border-white/20">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Cancel Subscription
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-300">
              Cancel your RaceTrainer subscription. Your membership stays active
              through the end of the current billing period.
            </p>
          </div>
          {customerPortalLink ? (
            <a
              href={customerPortalLink}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 rounded-md border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 dark:border-red-400 dark:text-red-300 dark:hover:bg-red-950/30"
            >
              Cancel Subscription
            </a>
          ) : (
            <Link
              to="/support"
              className="shrink-0 rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-white/10"
            >
              Contact support
            </Link>
          )}
        </div>
      </section>

      <div
        className="text-xs text-center my-10"
        style={{
          color:
            localSettings?.updatedAt &&
            new Date(localSettings?.updatedAt).getTime() > Date.now() - 30000
              ? 'green'
              : 'black',
        }}
      >
        {updateSettingsMutation.isPending
          ? 'Saving settings...'
          : localSettings?.updatedAt
            ? `Settings last updated at ${new Date(
                localSettings?.updatedAt,
              ).toLocaleString()}`
            : ''}
      </div>

      <VersionLabel className="mt-8 pt-6 border-t border-border text-muted-foreground" />

      <div className="md:hidden mt-8 pt-6 border-t border-border flex flex-wrap gap-4 text-sm">
        <Link
          to="/support"
          className="text-muted-foreground hover:text-foreground"
        >
          Support
        </Link>
        {profile?.user?.isAdmin ? (
          <Link
            to="/admin"
            className="text-muted-foreground hover:text-foreground"
          >
            Admin
          </Link>
        ) : null}
        <Link
          to="/logout"
          className="text-muted-foreground hover:text-foreground"
        >
          Logout
        </Link>
      </div>
    </div>
  );
}

export default Settings;
